import { Tabs } from '@chakra-ui/react'
import { mdiAlertCircleOutline, mdiThumbUpOutline } from '@mdi/js'
import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import NotificationBadge from '@/components/notificationBadge'
import Search from '@/components/search'
import { Box } from '@/components/ui/box'
import { Container } from '@/components/ui/container'
import { Flex } from '@/components/ui/flex'
import type { IApproval, ICompliance, TType } from '@/types/tasksApprovals'

import EmptyState from '../emptyState'
import AllTab from './all'
import ApprovalsTab from './approvals'
import TasksTab from './tasks'

const ItemsTabs = ({
    handleTabSwitch,
    all,
    approvals,
    activeTab = 0,
    tasks,
    notifications,
    onSearch,
    searchValue,
}: {
    handleTabSwitch: (id: TType) => void
    all: {
        tasks: ICompliance[]
        approvals: IApproval[]
    }
    approvals: IApproval[]
    activeTab: number
    tasks: ICompliance[]
    notifications: { tasks: number; approvals: number }
    onSearch: (e: ChangeEvent<HTMLInputElement>) => void
    searchValue: string
}) => {
    const { t } = useTranslation()
    const values: TType[] = ['all', 'tasks', 'approvals']
    const onActiveTabChange: NonNullable<Tabs.RootProps['onValueChange']> = (details) => {
        handleTabSwitch(details.value as TType)
    }
    return (
        <Container maxWidth="unset" h="100%" p="0">
            <Tabs.Root
                variant="dashboard"
                display="flex"
                flexDirection="column"
                value={values[activeTab]}
                onValueChange={onActiveTabChange}
            >
                <Flex
                    direction={['column', 'column', 'row']}
                    justifyContent={['space-between']}
                    mr={['16px', '16px', '24px', '24px']}
                    alignItems={['flex-start', 'flex-start', 'flex-start', 'center']}
                >
                    <Tabs.List mr={['16px', '24px']}>
                        <Tabs.Trigger value={values[0]}>{t('tasksApprovals.tabs.all')}</Tabs.Trigger>
                        <Tabs.Trigger value={values[1]}>
                            {t('tasksApprovals.tabs.tasks')}
                            {activeTab !== 1 && <NotificationBadge total={notifications.tasks} />}
                        </Tabs.Trigger>
                        <Tabs.Trigger value={values[2]}>
                            {t('tasksApprovals.tabs.approvals')}
                            {activeTab !== 2 && <NotificationBadge total={notifications.approvals} />}
                        </Tabs.Trigger>
                    </Tabs.List>
                    <Flex
                        mt={['16px', '16px', '16px', '0']}
                        mb={['8px', '8px', '0', '0']}
                        flexDir="row"
                        alignItems={['center', 'center', 'flex-end', 'flex-end']}
                        width={['100%', '100%', 'auto']}
                    >
                        <Search onSearch={onSearch} searchValue={searchValue} />
                    </Flex>
                </Flex>
                {(approvals.length > 0 || tasks.length > 0) && (
                    <Box overflow="auto" scrollbarGutter="stable">
                        <Tabs.Content value={values[0]}>
                            <AllTab approvals={all.approvals} tasks={all.tasks} />
                        </Tabs.Content>
                        <Tabs.Content value={values[1]}>
                            <TasksTab tasks={tasks} />
                        </Tabs.Content>
                        <Tabs.Content value={values[2]}>
                            <ApprovalsTab approvals={approvals} />
                        </Tabs.Content>
                    </Box>
                )}

                {approvals.length === 0 && tasks.length === 0 && searchValue && (
                    <EmptyState
                        title={t('tasksApprovals.empty.noResults.title')}
                        message={t('tasksApprovals.empty.noResults.message')}
                        icon={mdiAlertCircleOutline}
                        iconColor="var(--chakra-colors-status-error)"
                    />
                )}

                {approvals.length === 0 && tasks.length === 0 && !searchValue && (
                    <EmptyState
                        title={t('tasksApprovals.empty.noData.title')}
                        message={t('tasksApprovals.empty.noData.message')}
                        icon={mdiThumbUpOutline}
                        iconColor="var(--chakra-colors-tertiary-green-500)"
                    />
                )}
            </Tabs.Root>
        </Container>
    )
}

export default ItemsTabs
